import type { ManagerGetCampaignModel, CampaignTypes } from 'entities/campaign';
import { useRunCampaign } from 'entities/campaign';
import { AlertBox } from 'shared/ui/AlertBox';
import { useContractorDateWithTimeZone } from 'shared/lib/timezones';
import { Button } from 'shared/ui/Button';
import { useEffect } from 'react';
import { useModalsActions } from 'shared/ui/SideModal';
import { create } from 'zustand';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';

import { getAlertTextManager, OverbudgetAlert, BUDGET_SPENT_THRESHOLD_80 } from './Overbudget';
import { ReactivateCampaign } from './ReactivateCampaign';
import { ApproveCampaign } from './ApproveCampaign';
import { campaignTypeToTextMapper } from './campaign-type-text-mapper';

export interface ManagerCampaignDetailsAlertBoxesProps {
  campaign: ManagerGetCampaignModel;
  afterCampaignReactivate?: () => void;
}

const campaignAlertText: Record<keyof typeof CampaignTypes | 'salesOther', string> = {
  appt: 'This campaign may receive some appointments that have already started processing.',
  lead: 'This campaign is not receiving any leads.',
  sales:
    'Sales campaign is currently deactivated due to the payment method. To activate this campaign, please update the payment method to "Other".',
  salesOther: 'Campaign is not receiving any leads or appointments.',
};

const getDeactivatedCampaignText = ({
  isSales,
  isPaymentMethodChanged,
  type,
  isOther,
}: {
  isSales: boolean;
  isPaymentMethodChanged: boolean;
  type: ManagerGetCampaignModel['type'];
  isOther: boolean;
}) => {
  if (isSales && isPaymentMethodChanged) {
    return campaignAlertText[type as 'sales'];
  }

  if (type === 'lead' || type === 'appt') {
    return campaignAlertText[type];
  }

  if (isSales && isOther) {
    return campaignAlertText.salesOther;
  }

  return null;
};

interface isJustLaunchedStoreModel {
  isJustLaunched: boolean;
  actions: {
    activateIsJustLaunched: () => void;
    deactivateIsJustLaunched: () => void;
  };
}

const useIsJustLaunchedStore = create<isJustLaunchedStoreModel>((set) => ({
  isJustLaunched: false,
  actions: {
    activateIsJustLaunched: () => set(() => ({ isJustLaunched: true })),
    deactivateIsJustLaunched: () => set(() => ({ isJustLaunched: false })),
  },
}));

const useCampaignJustLaunched = () => useIsJustLaunchedStore((state) => state.isJustLaunched);
const useCampaignJustLaunchedActions = () => useIsJustLaunchedStore((state) => state.actions);

export function ManagerCampaignDetailsAlertBoxes({
  campaign,
  afterCampaignReactivate,
}: ManagerCampaignDetailsAlertBoxesProps) {
  const {
    type,
    id,
    status,
    pendingChanges,
    isActive,
    contractor: { paymentMethod },
    spentToBudgetPercent,
  } = campaign;
  const isCredit = paymentMethod === 'stripe';
  const isOther = paymentMethod === 'other';
  const isSales = type === 'sales';
  const isDeactivated = status === 'deactivated';
  const isPaymentMethodChanged = isCredit && isSales && isDeactivated;

  const getPendingDate = useContractorDateWithTimeZone();

  const formattedAppliesAt = pendingChanges && getPendingDate(pendingChanges.appliesAt);
  const campaignTypeText = campaignTypeToTextMapper[type];
  const deactivatedCampaignText = getDeactivatedCampaignText({
    isPaymentMethodChanged,
    type,
    isSales,
    isOther,
  });
  const overBudgetAlertText = getAlertTextManager({
    status,
    spentToBudgetPercent,
  });

  const { addBeforeCloseEvent, removeBeforeCloseEvent } = useModalsActions();
  const isCampaignJustLaunched = useCampaignJustLaunched();
  const { activateIsJustLaunched, deactivateIsJustLaunched } = useCampaignJustLaunchedActions();

  useEffect(() => {
    addBeforeCloseEvent(() => {
      deactivateIsJustLaunched();
      removeBeforeCloseEvent();
    });
  }, []);

  const runCampaignQuery = useRunCampaign(id, activateIsJustLaunched);
  const runNowBtnHandler = () => runCampaignQuery.mutateAsync();

  const willCampaignPausedNextCycle = pendingChanges?.attributes.some((attr) => attr.name === 'pause');
  const willCampaignLaunchedNextCycle = pendingChanges?.attributes.some((attr) => attr.name === 'running');
  const showSetToPauseAlert = status === 'running' && !isActive && willCampaignPausedNextCycle;
  const showSetToRunAlert = status === 'paused' && isActive && willCampaignLaunchedNextCycle;
  const showJustLaunchedAlert = status === 'running' && isActive && isCampaignJustLaunched;
  const showInReviewAlert = status === 'inReview';
  const showNoCcAddedAlert = status === 'noCcAdded';
  const showDeactivatedAlert = isDeactivated && deactivatedCampaignText;
  const showOverbudgetAlert = spentToBudgetPercent >= BUDGET_SPENT_THRESHOLD_80;

  const showAlertBoxes =
    showDeactivatedAlert ||
    showSetToPauseAlert ||
    showSetToRunAlert ||
    showJustLaunchedAlert ||
    showInReviewAlert ||
    showNoCcAddedAlert ||
    showOverbudgetAlert;

  return (
    <>
      {showAlertBoxes && (
        <div
          className="flex flex-col gap-4"
          {...getDataAutoTestAttributes(['campaign-status-message']).attributes}
        >
          {showDeactivatedAlert && (
            <AlertBox
              className="border-red-100"
              iconClass="text-red-500"
              iconName="error_outline"
            >
              <div className="text-sm">
                <div className="font-medium">Campaign is deactivated</div>
                <div className="mt-2 text-slate-700">{deactivatedCampaignText}</div>
                {!(isSales && !isOther) && (
                  <div>
                    <ReactivateCampaign
                      campaign={campaign}
                      onSuccess={afterCampaignReactivate}
                    />
                  </div>
                )}
              </div>
            </AlertBox>
          )}
          {showSetToPauseAlert && (
            <AlertBox
              className="border-orange-100"
              bodyClass="flex flex-col gap-4 items-start"
              iconClass="text-orange-500"
              iconName="update"
              dataTestId="alert-set-campaign-to-pause"
            >
              <div className="font-medium">{`Campaign is set to 'pause'`}</div>
              <div className="text-sm text-slate-700">
                {`This campaign will stop receiving ${campaignTypeText} on ${formattedAppliesAt}.`}
              </div>
            </AlertBox>
          )}
          {showSetToRunAlert && (
            <AlertBox
              className="border-orange-100"
              bodyClass="flex flex-col gap-4 items-start"
              iconClass="text-orange-500"
              iconName="update"
              dataTestId="alert-set-campaign-to-run"
            >
              <div className="font-medium">{`Campaign is set to 'run'`}</div>
              <div className="text-sm text-slate-700">
                {`This campaign will start receiving ${campaignTypeText} on ${formattedAppliesAt}.`}
              </div>
              <Button
                variant="outline"
                size="medium"
                onClick={runNowBtnHandler}
                disabled={runCampaignQuery.isLoading}
                {...getDataAutoTestAttributes([`campaign-details-run-now-btn`])}
              >
                Run now
              </Button>
            </AlertBox>
          )}
          {showJustLaunchedAlert && (
            <AlertBox
              className="border-deep-green-100"
              bodyClass="flex flex-col gap-4 items-start"
              iconClass="text-deep-green-500"
              iconName="check_circle"
            >
              <div className="font-medium">Campaign is running</div>
              <div className="text-sm text-slate-700">
                {`Please note that the just-launched campaign might receive fewer ${campaignTypeText} until the upcoming Monday.`}
              </div>
            </AlertBox>
          )}
          {showInReviewAlert && (
            <AlertBox
              className="border-deep-blue-100"
              bodyClass="flex flex-col gap-4 items-start"
              iconClass="text-deep-blue-500"
              iconName="info"
            >
              <div className="font-medium">This campaign is under moderation</div>
              <ApproveCampaign campaign={campaign} />
            </AlertBox>
          )}
          {showNoCcAddedAlert && (
            <AlertBox
              className="border-orange-100"
              iconClass="text-orange-500"
              iconName="warning_amber"
            >
              <div className="text-sm">
                <div className="font-medium">Campaign will not run until the contractor adds a Credit Card</div>
                <div className="mt-2 text-slate-700">
                  Once a Credit Card is added, the campaign will begin on the following Monday.
                </div>
              </div>
            </AlertBox>
          )}
          {showOverbudgetAlert && <OverbudgetAlert alertText={overBudgetAlertText} />}
        </div>
      )}
    </>
  );
}
